import { useState } from "react";
import { PosTask } from "../interfaces/Task";
import Moment from 'moment';
import ConfirmationDialog from "../elements/ConfirmationDialog";
import EditPosTaskRepetition from "./PosTaskTypes/EditPosTaskRepetition";
import EditPosTaskExercises from "./PosTaskTypes/EditPosTaskExercises";
import EditPosTaskExplanationText from "./PosTaskTypes/EditPosTaskExplanationText";
import EditPosTaskReport from "./PosTaskTypes/EditPosTaskReport";
import EditPosTaskTeacherFeedback from "./PosTaskTypes/EditPosTaskTeacherFeedback";
import EditPosTaskTME from "./PosTaskTypes/EditPosTaskTME";
import EditPosTaskInteractionActivity from "./PosTaskTypes/EditPosTaskInteractionActivity";
import EditPosTaskVideo from "./PosTaskTypes/EditPosTaskVideo";
import EditPosTaskText from "./PosTaskTypes/EditPosTaskText";
import EditPosTaskPhoto from "./PosTaskTypes/EditPosTaskPhoto";
import EditPosTaskGame from "./PosTaskTypes/EditPosTaskGame";
import EditPosTaskDialog from "./PosTaskTypes/EditPosTaskDialog";
import EditPosTaskAudio from "./PosTaskTypes/EditPosTaskAudio";
import { useTranslation } from "react-i18next";

export interface PosTaskTeaserProps {
    task_id: string;
    task_language?: string;
    postask: PosTask;
    position: number;
    deleteCallback: CallableFunction;
    updateCallback: CallableFunction;
    cloneCallback: CallableFunction;
    sortUpCallback: CallableFunction;
    sortDownCallback: CallableFunction;
    cloneAsCallback: CallableFunction;
}

export default function PosTaskTeaser(props: PosTaskTeaserProps) {
    const { t } = useTranslation();
    
    const [showConfirm, setShowConfirm] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const handleDelete = () => {
        props.deleteCallback(props.postask._id)
    }

    const askForConfirmation = () => {
        setShowConfirm(true);
    }

    const cancelDelete = () => {
        setShowConfirm(false);
    }
    
    const handleShowEdit = () => {
        setShowEdit(true);
    }

    const handleHideEdit = () => {
        setShowEdit(false);
    }

    const handleSave = (postask: PosTask) => {
        props.updateCallback(postask, props.position);
        setShowEdit(false);
    }

    const handleSortUp = () => {
        props.sortUpCallback(props.position);
    }

    const handleSortDown = () => {
        props.sortDownCallback(props.position);
    }

    const handleClone = () => {
        props.cloneCallback(props.position);
    }

    const handleCloneAs = (id: string, direction: string) => {
        props.cloneAsCallback(id, direction);
    }

    const decodePostaskType = (type: string) => {
        switch (type) {
            case 'audio':
                return t('Audio');
            case 'dialog':
                return t('Written dialogue');
            case 'photo':
                return t('Photo');
            case 'text':
                return t('Text');
            case 'video':
                return t('Video');
            case 'game':
                return t('Game');
            case 'tme':
                return t('Task model and exercises');
            case 'repetition':
                return t('Task repetition');
            case 'interactionactivity':
                return t('Interaction activity');
            case 'explanationtext':
                return t('Explicit explanation');
            case 'report':
                return t('Written to Oral / Oral to Written');
            case 'teacherfeedback':
                return t('Teacher feedback');
            case 'exercises':
                return t('Exercises');
            default:
                return type;
        }
    }

    return(
        <div className={"post-task-teaser-container " + props.postask.type } key={ 'post-task-' + props.postask._id }>
            <h4>{ props.postask.name }</h4>
            <div className="type">{ t("Type") }: { decodePostaskType(props.postask.type) }</div>
            <div className="updated">{ Moment(props.postask.updated).format('DD/MM/YYYY') }</div>
            <div className="actions">
                <a className="sort-up" onClick={ handleSortUp } title={ t("Move Up") }></a>
                <a className="sort-down" onClick={ handleSortDown } title={ t("Move Up") }></a>
                <a className="clone-link" onClick={ handleClone } title={ 'Clone pre-task' }></a>
                <a className="edit-link" onClick={ handleShowEdit } title={ 'Edit pre-task' }></a>
                <a className="delete-link" onClick={ askForConfirmation } title={ 'Delete pre-task' }></a>
            </div>
            { showConfirm &&
                <ConfirmationDialog
                    message={ t("Are you sure you want to delete the post-task?") + '<strong>' + props.postask.name + '</strong>?' + t("This action cannot be undone.") }
                    confirmLabel={ t("Delete") } cancelLabel={ t("Cancel") }
                    confirmCallback={ handleDelete } cancelCallback={ cancelDelete } />
            }
            { props.postask.type == 'tme' && showEdit &&
                <EditPosTaskTME task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id }
                    position={ props.position } saveForm={ handleSave } closeForm={ handleHideEdit } />
            }
            { props.postask.type == 'repetition' && showEdit &&
                <EditPosTaskRepetition task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id }
                    position={ props.position } saveForm={ handleSave } closeForm={ handleHideEdit } />
            }
            { props.postask.type == 'interactionactivity' && showEdit &&
                <EditPosTaskInteractionActivity task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id }
                    position={ props.position } saveForm={ handleSave } closeForm={ handleHideEdit } />
            }
            { props.postask.type == 'explanationtext' && showEdit &&
                <EditPosTaskExplanationText task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id }
                    position={ props.position } saveForm={ handleSave } closeForm={ handleHideEdit } />
            }
            { props.postask.type == 'report' && showEdit &&
                <EditPosTaskReport task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id }
                    position={ props.position } saveForm={ handleSave } closeForm={ handleHideEdit } />
            }
            { props.postask.type == 'teacherfeedback' && showEdit &&
                <EditPosTaskTeacherFeedback task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id }
                    position={ props.position } saveForm={ handleSave } closeForm={ handleHideEdit } />
            }
            { props.postask.type == 'exercises' && showEdit &&
                <EditPosTaskExercises task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id }
                    position={ props.position } saveForm={ handleSave } closeForm={ handleHideEdit } />
            }
            { props.postask.type == 'audio' && showEdit &&
                <EditPosTaskAudio task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id } position={ props.position }
                    saveForm={ handleSave } closeForm={ handleHideEdit } cloneAs={ handleCloneAs } />
            }
            { props.postask.type == 'dialog' && showEdit &&
                <EditPosTaskDialog task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id } position={ props.position }
                    saveForm={ handleSave } closeForm={ handleHideEdit } cloneAs={ handleCloneAs } />
            }
            { props.postask.type == 'game' && showEdit &&
                <EditPosTaskGame task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id } position={ props.position }
                    saveForm={ handleSave } closeForm={ handleHideEdit } cloneAs={ handleCloneAs } />
            }
            { props.postask.type == 'photo' && showEdit &&
                <EditPosTaskPhoto task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id } position={ props.position }
                    saveForm={ handleSave } closeForm={ handleHideEdit } cloneAs={ handleCloneAs } />
            }
            { props.postask.type == 'text' && showEdit &&
                <EditPosTaskText task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id } position={ props.position }
                    saveForm={ handleSave } closeForm={ handleHideEdit } cloneAs={ handleCloneAs } />
            }
            { props.postask.type == 'video' && showEdit &&
                <EditPosTaskVideo task_id={ props.task_id } task_language={ props.task_language } id={ props.postask._id } position={ props.position }
                    saveForm={ handleSave } closeForm={ handleHideEdit } cloneAs={ handleCloneAs } />
            }

        </div>
    );
}